import React from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Icon from "@/src/components/atom/Icon";
import CloseSvg from "@/src/assets/icons/icon-cancle-black.svg";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";

interface RejectReasonViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  rejectReason?: string;
  title?: string;
  onEditContract?: () => void;
  contractId?: number;
  hideEditButton?: boolean;
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    padding: 0;
    background: white;
    overflow: hidden;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-footer {
    display: none;
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid #e7edf0;
`;

const HeaderTitle = styled.div`
  flex: 1 1 0;
  color: black;
  font-size: 20px;
  font-family: "Pretendard";
  font-weight: 700;
  line-height: 30px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  background: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const ReasonText = styled.div`
  color: #222729;
  font-size: 16px;
  font-family: "Pretendard";
  font-weight: 400;
  line-height: 24px;
  word-wrap: break-word;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 20px 24px;
`;

const FooterButton = styled.button<{ variant: "tertiary" | "primary" }>`
  flex: 1 1 0;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border: none;
  cursor: pointer;
  font-size: 15px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ variant }) =>
    variant === "tertiary"
      ? `
    background: white;
    color: #222729;
    border: 1px solid #d9d9d9;
    
    &:hover {
      background: #f5f5f5;
    }
  `
      : `
    background: #1C3D61;
    color: white;
    
    &:hover {
      background: #0F2640;
    }
  `}
`;

export const RejectReasonViewModal: React.FC<RejectReasonViewModalProps> = ({
  isOpen,
  onClose,
  rejectReason,
  title,
  onEditContract,
  contractId,
  hideEditButton = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleEditContract = () => {
    if (onEditContract) {
      onEditContract();
    } else if (contractId) {
      // Navigate to contract edit page if no custom handler provided
      const currentPath = window.location.pathname;
      const isExporter =
        currentPath.includes("/export/") || currentPath.includes("/exporter/");

      if (isExporter) {
        navigate(`${EXPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${contractId}`);
      } else {
        navigate(`${IMPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${contractId}`);
      }
    }
    onClose();
  };

  return (
    <StyledModal 
      open={isOpen} 
      onCancel={onClose} 
      width={520} 
      centered
      closeIcon={false}
      footer={null}
    >
      <ModalContainer>
        <ModalHeader>
          <HeaderTitle>{title || t("contract:reject.viewTitle")}</HeaderTitle>
          <CloseButton onClick={onClose}>
            <img src={CloseSvg} alt="close" />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <ReasonText>
            {rejectReason ||
              t("contract:reject.noReason", "반려 사유가 없습니다.")}
          </ReasonText>
        </ModalContent>

        {!hideEditButton && (
          <ModalFooter>
            <FooterButton variant="tertiary" onClick={onClose}>
              {t("common:cancel", "취소")}
            </FooterButton>
            <FooterButton variant="primary" onClick={handleEditContract}>
              {t("contract:reject.editContract", "계약 수정하기")}
            </FooterButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </StyledModal>
  );
};

export default RejectReasonViewModal;
